import { useEffect, useState } from "react";
import bundled from "../data/plans.json";
import type { Plan, PlanPrice, PlansPayload } from "../types/plans";
import { MESSAGES, type Locale } from "../i18n/messages";

const bundledPayload = bundled as unknown as PlansPayload;

async function fetchLive(signal: AbortSignal, timeoutMs: number): Promise<PlansPayload | null> {
  const timeout = AbortSignal.timeout ? AbortSignal.timeout(timeoutMs) : signal
  const combined = AbortSignal.any ? AbortSignal.any([signal, timeout]) : signal
  try {
    const res = await fetch("/api/plans", { signal: combined, headers: { Accept: "application/json" } })
    if (!res.ok) return null
    const data = (await res.json()) as PlansPayload
    if (!data || !Array.isArray(data.plans) || !data.meta) return null
    return data
  } catch {
    return null
  }
}

export interface PlansState {
  data: PlansPayload
  source: "api" | "bundle"
  loading: boolean
}

/**
 * Loads travel plans. Prefers the live /api/plans endpoint (Docker deployment,
 * always up to date) and falls back to the snapshot bundled at build time
 * (Cloudflare Pages / static hosting). When both exist, the newer wins.
 */
export function usePlans(): PlansState {
  const [state, setState] = useState<PlansState>({
    data: bundledPayload,
    source: "bundle",
    loading: true,
  })

  useEffect(() => {
    const ctrl = new AbortController()
    let cancelled = false

    fetchLive(ctrl.signal, 1500).then((live) => {
      if (cancelled || !live) {
        setState((s) => ({ ...s, loading: false }))
        return
      }
      const liveIsNewer =
        Date.parse(live.meta.scrapedAt) > Date.parse(bundledPayload.meta.scrapedAt)
      setState({
        data: liveIsNewer ? live : bundledPayload,
        source: liveIsNewer ? "api" : "bundle",
        loading: false,
      })
    })

    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [])

  return state
}

export function formatPrice(price: PlanPrice, locale: Locale = "es"): string {
  if (price.amount == null) return MESSAGES[locale].plan.priceOnRequest
  return new Intl.NumberFormat(MESSAGES[locale].localeTag, {
    style: "currency",
    currency: price.currency,
    maximumFractionDigits: price.amount % 1 === 0 ? 0 : 2,
  }).format(price.amount)
}

export function formatDates(dates: string[], locale: Locale = "es"): string {
  if (!dates.length) return MESSAGES[locale].plan.datesOnRequest
  const fmt = new Intl.DateTimeFormat(MESSAGES[locale].localeTag, { day: "numeric", month: "long", year: "numeric" })
  return dates
    .map((d) => new Date(`${d}T00:00:00`))
    .map((d) => (Number.isNaN(d.getTime()) ? d : fmt.format(d)))
    .join(" · ")
}

export function planIsAvailable(plan: Plan): boolean {
  return plan.availability !== "agotado"
}
